import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Loader2, Trash2, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { CARD, LBL, fmtNum } from "./axiom";

const REQUIRED_COLS = ["LOB", "Teams", "Category", "Sub Category", "Qtl Call Reason", "Call Duration", "Lang Id", "Login", "Name", "Location", "Benchmark", "Created / Date"];

export const UploadTab = ({ summary, refresh }) => {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/aht/upload", fd, { timeout: 600000 });
      toast.success(`${fmtNum(data.inserted)} rows ingested (${fmtNum(data.skipped)} skipped)`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/aht/dataset");
      toast.success("Dataset cleared");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
      <div>
        <div
          data-testid="aht-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed bg-thcard px-8 py-20 text-center transition-colors ${
            dragOver ? "border-thcyan/60 bg-thcyan/10" : "border-thborder/20 hover:border-thcyan/40"}`}
        >
          {busy ? <Loader2 className="mb-4 h-10 w-10 animate-spin text-thcyan" /> : <UploadCloud className="mb-4 h-10 w-10 text-thcyan" />}
          <p className="font-heading text-lg font-semibold text-thstrong">{busy ? "Parsing & ingesting... (large files can take a minute)" : "Drop call-detail exports here"}</p>
          <p className="mt-2 text-sm text-thdim">or click to browse · .csv .tsv .xls .xlsx · multiple files supported</p>
          <p className={`${LBL} mt-4`}>Only rows with Benchmark = Yes are processed</p>
          <input ref={inputRef} type="file" multiple accept=".csv,.tsv,.txt,.xls,.xlsx" className="hidden"
            data-testid="aht-file-input" onChange={(e) => upload(e.target.files)} />
        </div>

        {summary.total > 0 && (
          <div className="mt-6 flex items-center justify-between rounded-2xl border border-thmint/30 bg-thmint/10 px-5 py-4">
            <p className="flex items-center gap-2 text-sm text-thbody">
              <CheckCircle2 className="h-4 w-4 text-thmint" />
              Active dataset: <span className="font-mono-hud font-bold text-thmint">{fmtNum(summary.total)} rows</span>
            </p>
            <button data-testid="aht-clear-dataset" onClick={clearAll}
              className="flex items-center gap-1.5 text-xs text-thdim hover:text-red-500">
              <Trash2 className="h-3.5 w-3.5" /> Clear dataset
            </button>
          </div>
        )}

        {(summary.files || []).length > 0 && (
          <div className={`${CARD} mt-6 p-5`}>
            <p className={`${LBL} mb-3`}>Uploaded files</p>
            {summary.files.map((f, i) => (
              <div key={i} className="flex items-center gap-3 border-b border-thborder/6 py-2 text-sm last:border-0">
                <FileSpreadsheet className="h-4 w-4 shrink-0 text-thcyan" />
                <span className="flex-1 truncate text-thbody">{f.file}</span>
                <span className="font-mono-hud text-xs text-thdim">{fmtNum(f.rows)} rows · {f.uploaded_by}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${CARD} p-6`}>
        <p className={`${LBL} mb-4 text-thcyan`}>Expected columns</p>
        <div className="flex flex-wrap gap-2">
          {REQUIRED_COLS.map((c) => (
            <span key={c} className="rounded-full bg-thcyan/10 px-3 py-1 font-mono-hud text-[10px] text-thcyan">{c}</span>
          ))}
        </div>
        <ul className="mt-6 space-y-3 text-xs leading-relaxed text-thmute">
          <li>· 3-level hierarchy: <b>Category → Sub Category → Qtl Call Reason</b> (sub-sub).</li>
          <li>· <b>Created</b> (or Date) unlocks Date, Trends and Benchmark weekly views.</li>
          <li>· <b>Login / Name</b> unlock the Agents tab.</li>
          <li>· Rows with missing Call Duration are skipped.</li>
          <li>· Uploads append to the current dataset — clear to start fresh.</li>
        </ul>
      </div>
    </div>
  );
};
